// API Services
// In production, these would make actual API calls

import { backendOTPService } from './emailService';

// Simulated delay for API calls
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// BATFaaS API - Bank Account Title Fetch as a Service
export const batfaasAPI = {
  // Fetch bank account title from account number
  fetchAccountTitle: async (bankCode, accountNumber) => {
    await delay(1500); // Simulate API latency

    // Mock responses based on account numbers
    const mockAccounts = {
      '1234567890123': { title: 'MUHAMMAD AHMED KHAN', bank: 'HBL', status: 'active' },
      '9876543210987': { title: 'FATIMA ZAHRA', bank: 'MCB', status: 'active' },
      '5555666677778888': { title: 'HASSAN ALI', bank: 'UBL', status: 'active' },
      '1111222233334444': { title: 'SARA MALIK', bank: 'ABL', status: 'active' },
      '9999888877776666': { title: 'USMAN GHANI', bank: 'HBL', status: 'active' },
    };

    const account = mockAccounts[accountNumber];

    if (account) {
      return {
        success: true,
        data: {
          accountNumber,
          accountTitle: account.title,
          bankName: account.bank,
          status: account.status,
          fetchedAt: new Date().toISOString(),
        }
      };
    }

    // For demo, generate a random title for unknown accounts
    return {
      success: true,
      data: {
        accountNumber,
        accountTitle: 'DEMO USER ACCOUNT',
        bankName: bankCode || 'Unknown Bank',
        status: 'active',
        fetchedAt: new Date().toISOString(),
      }
    };
  },

  // Verify if account title matches KYC name (Exact Match Only)
  verifyAccountMatch: (fetchedTitle, kycName) => {
    const normalizedFetched = fetchedTitle.toUpperCase().trim().replace(/\s+/g, ' ');
    const normalizedKyc = kycName.toUpperCase().trim().replace(/\s+/g, ' ');

    // Exact match only
    if (normalizedFetched === normalizedKyc) {
      return { matched: true, reason: 'Exact match' };
    }

    // Any difference = mismatch
    return { matched: false, reason: 'Name mismatch' };
  }
};

// Sidobe WhatsApp API Configuration
const WHATSAPP_CONFIG = {
  API_URL: 'https://api.sidobe.com/wa/v1',
  SECRET_KEY: 'osacPqDDMLwjusdgVGgJFgdSMHGTJymfeheDgpvnGaCwMMlMzg',
};

// WhatsApp API - Automated messaging via Sidobe
export const whatsappAPI = {
  // Format phone number for WhatsApp (e164 format with + prefix)
  formatPhoneNumber: (phoneNumber) => {
    // Remove all non-numeric characters except +
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');

    // If starts with 0, replace with Pakistan country code
    if (cleaned.startsWith('0')) {
      cleaned = '+92' + cleaned.substring(1);
    }

    // Ensure + prefix exists
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }

    return cleaned;
  },

  // Send message via Sidobe WhatsApp API
  sendMessage: async (phoneNumber, message) => {
    const formattedPhone = whatsappAPI.formatPhoneNumber(phoneNumber);

    console.log(`[Sidobe WhatsApp API] Sending message to ${formattedPhone}`);

    try {
      const response = await fetch(`${WHATSAPP_CONFIG.API_URL}/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Secret-Key': WHATSAPP_CONFIG.SECRET_KEY,
        },
        body: JSON.stringify({
          phone: formattedPhone,
          message: message,
        }),
      });

      const data = await response.json();

      console.log(`[Sidobe WhatsApp API] Response:`, data);

      if (response.ok) {
        console.log(`[Sidobe WhatsApp API] Message sent successfully:`, data);
        return {
          success: true,
          messageId: data.data?.message_id || `WA-${Date.now()}`,
          sentAt: new Date().toISOString(),
          phone: formattedPhone,
        };
      } else {
        console.error('[Sidobe WhatsApp API] Error:', data);
        return {
          success: false,
          error: data.message || 'Failed to send message',
          phone: formattedPhone,
        };
      }
    } catch (error) {
      console.error('[Sidobe WhatsApp API] Network error:', error);
      return {
        success: false,
        error: error.message || 'Network error',
        phone: formattedPhone,
      };
    }
  },

  // Send OTP via WhatsApp
  sendOTP: async (phoneNumber, otpCode) => {
    const message = `*AnbPay - Account Verification*

Your OTP Code: *${otpCode}*

This code will expire in 5 minutes.

Do NOT share this code with anyone.

If you did not request this, please ignore this message.`;

    return whatsappAPI.sendMessage(phoneNumber, message);
  },

  // Send reference number with payment details (automated)
  sendReferenceNumber: async (phoneNumber, refNumber, accountDetails) => {
    const message = `*AnbPay - Payment Reference*

Your Reference Number:
*${refNumber}*

Transfer to this Account:
Bank: ${accountDetails.bankName}
Account: ${accountDetails.accountNumber}
Title: ${accountDetails.accountTitle}

IMPORTANT: Include the reference number *${refNumber}* in your payment remarks/description.

Thank you for using AnbPay!`;

    return whatsappAPI.sendMessage(phoneNumber, message);
  },

  // Send payment confirmation (automated)
  sendPaymentConfirmation: async (phoneNumber, transactionId, amount, status) => {
    const statusMessages = {
      success: `*Payment Confirmed!*

Transaction: ${transactionId}
Amount: PKR ${amount.toLocaleString()}

Your account has been credited successfully.

Thank you for using AnbPay!`,
      failed: `*Payment Verification Failed*

Transaction: ${transactionId}

Please contact support for assistance.`,
      mismatch: `*Payment Review Required*

Transaction: ${transactionId}

Your payment is under review. We will notify you shortly.`,
      penalty: `*Payment Rejected - Details Mismatch*

Transaction: ${transactionId}
Amount: PKR ${amount.toLocaleString()}

Your payment details did not match. A penalty has been applied and funds will be returned.

Please contact support for assistance.`,
    };

    return whatsappAPI.sendMessage(phoneNumber, statusMessages[status] || statusMessages.failed);
  },

  // Send account whitelisted confirmation
  sendAccountWhitelisted: async (phoneNumber, accountTitle, bankName) => {
    const message = `*AnbPay - Account Whitelisted*

Your bank account has been successfully verified and whitelisted!

Account: ${accountTitle}
Bank: ${bankName}

You can now proceed with your top-up.

Thank you for using AnbPay!`;

    return whatsappAPI.sendMessage(phoneNumber, message);
  },
};

// Email API - For sending OTP and notifications via email
// Uses backend server (OTP generated on backend for security)
export const emailAPI = {
  // Send OTP via Email (uses backend service)
  sendOTP: async (email, phone, userName = '') => {
    console.log(`[Email API] Sending OTP to ${email}`);
    return backendOTPService.sendOTP(email, phone, userName);
  },

  // Verify OTP via backend
  verifyOTP: async (email, phone, otp) => {
    return backendOTPService.verifyOTP(email, phone, otp);
  },

  // Resend OTP
  resendOTP: async (email, phone, userName = '') => {
    return backendOTPService.resendOTP(email, phone, userName);
  },

  // Send payment confirmation via email
  sendPaymentConfirmation: async (email) => {
    await delay(800);
    console.log(`[Email API] Sending payment confirmation to ${email}`);
    return {
      success: true,
      messageId: `EMAIL-${Date.now()}`,
      sentAt: new Date().toISOString(),
    };
  },

  // Send account whitelisted email
  sendAccountWhitelisted: async (email) => {
    await delay(800);
    console.log(`[Email API] Sending whitelisted confirmation to ${email}`);
    return {
      success: true,
      messageId: `EMAIL-${Date.now()}`,
      sentAt: new Date().toISOString(),
    };
  },
};

// OTP Service - Uses backend for secure OTP generation and verification
export const otpService = {
  // Send OTP to user via backend (WhatsApp + Email)
  // OTP is generated on backend for security - never exposed to frontend
  sendOTP: async (phoneNumber, email, userName = '') => {
    try {
      const result = await backendOTPService.sendOTP(email, phoneNumber, userName);

      return {
        success: result.success,
        whatsapp: result.whatsapp,
        email: result.emailDelivery,
        expiresAt: result.expiresAt,
        // OTP is never sent to frontend for security
      };
    } catch (error) {
      console.error('[OTP Service] Backend error:', error);
      // No fallback - OTP must always be generated on backend for security
      return {
        success: false,
        error: 'Failed to send OTP. Please try again.',
        whatsapp: null,
        email: null,
      };
    }
  },

  // Verify OTP via backend (secure)
  verify: async (email, phone, inputOtp) => {
    try {
      const result = await backendOTPService.verifyOTP(email, phone, inputOtp);
      return {
        valid: result.valid,
        reason: result.reason,
      };
    } catch (error) {
      console.error('[OTP Service] Verify error:', error);
      return { valid: false, reason: 'Verification failed' };
    }
  },
};

// IBAN Verification API
export const ibanAPI = {
  // Validate IBAN format
  validateFormat: (iban) => {
    // Remove spaces and convert to uppercase
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();

    // Pakistan IBAN format: PK + 2 check digits + 4 bank code + 16 account number = 24 characters
    const pkIbanPattern = /^PK\d{2}[A-Z]{4}\d{16}$/;

    if (!pkIbanPattern.test(cleanIban)) {
      return {
        valid: false,
        error: 'Invalid IBAN format. Pakistan IBAN should be 24 characters starting with PK.',
      };
    }

    return { valid: true, iban: cleanIban };
  },

  // Extract account number from IBAN
  extractAccountNumber: (iban) => {
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    // Last 16 digits are the account number
    return cleanIban.slice(-16);
  },

  // Extract bank code from IBAN
  extractBankCode: (iban) => {
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    // Characters 5-8 are the bank code
    return cleanIban.slice(4, 8);
  },
};

// Document OCR API - For IBAN letter / cancelled cheque verification
export const documentOCR = {
  // Process IBAN letter or cancelled cheque image
  processDocument: async (imageFile, expectedData) => {
    await delay(2500); // Simulate OCR processing

    // In production, this would use actual OCR service (Tesseract.js, Google Vision, AWS Textract)
    // For demo, we'll simulate extraction based on the expected data

    return {
      success: true,
      data: {
        accountTitle: expectedData.accountTitle || 'EXTRACTED ACCOUNT TITLE',
        accountNumber: expectedData.accountNumber || '0000000000000000',
        iban: expectedData.iban || null,
        bankName: expectedData.bankName || 'EXTRACTED BANK',
        confidence: 85,
        rawText: 'Mock OCR extracted text from document...',
      },
      processedAt: new Date().toISOString(),
    };
  },

  // Verify document data matches KYC
  verifyDocument: (ocrData, kycName) => {
    const normalizedOcr = ocrData.accountTitle?.toUpperCase().trim().replace(/\s+/g, ' ') || '';
    const normalizedKyc = kycName?.toUpperCase().trim().replace(/\s+/g, ' ') || '';

    // Check if names match (all words from KYC must be in OCR)
    const kycWords = normalizedKyc.split(' ').filter(w => w.length > 0);
    const ocrWords = normalizedOcr.split(' ').filter(w => w.length > 0);

    const allKycWordsFound = kycWords.every(kycWord =>
      ocrWords.some(ocrWord => ocrWord === kycWord)
    );

    if (allKycWordsFound && kycWords.length > 0) {
      return { matched: true, reason: 'Account title matches KYC name' };
    }

    return { matched: false, reason: 'Account title does not match KYC name' };
  },
};

// OCR API Mock - Simulates receipt scanning
export const ocrAPI = {
  processReceipt: async (imageFile) => {
    await delay(2000); // Simulate OCR processing time

    // In production, this would use actual OCR service
    // Mock extraction based on random data or file analysis
    return {
      success: true,
      data: {
        senderName: 'EXTRACTED SENDER NAME',
        senderAccount: '1234567890123',
        receiverName: 'ANBPAY',
        receiverAccount: '00112233445566',
        amount: 50000,
        referenceNumber: null, // Will be extracted from remarks
        transactionDate: new Date().toISOString(),
        bankName: 'HBL',
        confidence: 85,
        rawText: 'Mock OCR extracted text...',
      },
      processedAt: new Date().toISOString(),
    };
  },

  // Enhanced extraction with reference number detection
  extractTransactionDetails: async (imageFile, expectedRefNumber) => {
    await delay(2500);

    // Simulate successful extraction
    return {
      success: true,
      data: {
        senderName: 'MUHAMMAD AHMED KHAN',
        amount: 50000,
        referenceNumber: expectedRefNumber, // In real OCR, this would be extracted
        transactionId: `BANK-TXN-${Date.now()}`,
        timestamp: new Date().toISOString(),
        confidence: {
          overall: 92,
          senderName: 95,
          amount: 98,
          referenceNumber: 88,
        }
      }
    };
  }
};

// Reference Number Generator
export const referenceGenerator = {
  generate: () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `REF-${year}${month}-${random}`;
  }
};

// Transaction API
export const transactionAPI = {
  create: async (transactionData) => {
    await delay(500);
    return {
      success: true,
      data: {
        id: `TXN-${Date.now()}`,
        ...transactionData,
        createdAt: new Date().toISOString(),
        status: 'pending_payment',
      }
    };
  },

  update: async (transactionId, updates) => {
    await delay(300);
    return {
      success: true,
      data: {
        id: transactionId,
        ...updates,
        updatedAt: new Date().toISOString(),
      }
    };
  },

  getById: async (transactionId) => {
    await delay(300);
    // Would fetch from database in production
    return {
      success: true,
      data: null, // Return from state instead
    };
  }
};

// Penalty calculation for mismatched transactions
export const penaltyService = {
  // Calculate penalty based on amount and mismatch type
  calculatePenalty: (amount, mismatchType) => {
    const penaltyRates = {
      name_mismatch: 0.05, // 5% penalty for name mismatch
      amount_mismatch: 0.03, // 3% penalty for amount mismatch
      reference_mismatch: 0.02, // 2% penalty for reference mismatch
      multiple_mismatch: 0.10, // 10% penalty for multiple mismatches
    };

    const rate = penaltyRates[mismatchType] || penaltyRates.multiple_mismatch;
    const penaltyAmount = Math.round(amount * rate);

    return {
      originalAmount: amount,
      penaltyRate: rate * 100,
      penaltyAmount,
      returnAmount: amount - penaltyAmount,
    };
  },
};

export default {
  batfaasAPI,
  whatsappAPI,
  emailAPI,
  otpService,
  ibanAPI,
  documentOCR,
  ocrAPI,
  referenceGenerator,
  transactionAPI,
  penaltyService,
};
